<template>
    <div class="space-y-4">
        <label
            v-if="$slots.default || inputLabel"
            class="text-body-sm font-semibold text-black"
            @click="handleFocus"
        >
            <slot>{{ capitaliseFirst(inputLabel) }}</slot>
        </label>

        <div
            :class="[disabled ? 'cursor-not-allowed bg-primary-5 border-primary-20 text-primary-60' : 'border-primary-80', this.minWidthInput, this.error ? 'border-semantic-error' : '', sizeClass, {'bg-white': whiteBackground}]"
            class="w-full flex items-center gap-4 border rounded py-12 px-16 outline-none focus:border-semantic-info h-44"
        >
            <tooltip v-if="showWarningIcon" :content="invalidDateMessage" position="left">
                <div>
                    <svg-icon type="warning" class="max-w-16 max-h-16 text-semantic-warning -mt-px"></svg-icon>
                </div>
            </tooltip>
            <flat-pickr
                ref="picker"
                :class="disabled ? 'cursor-not-allowed !bg-primary-5' : ''"
                :config="config"
                :disabled="disabled"
                :placeholder="placeholder"
                v-model="date"
                @on-change="onEndChange"
            ></flat-pickr>
            <div>
                <span v-if="clearable && value" @click="clearInput">
                    <svg-icon
                        :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
                        class="w-18 h-18 text-primary-20 hover:text-semantic-error transition duration-50"
                        type="close-circle"
                    />
                </span>
                <span @click="handleFocus">
                    <svg-icon
                        :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
                        class="w-18 h-18"
                        type="calendar-today"
                    />
                </span>
            </div>
        </div>
        <p v-if="error" class="text-semantic-error text-body-sm">{{ error }}</p>
    </div>

</template>

<script>
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr';
import SvgIcon from '../ui/SvgIcon.vue';
import moment from 'moment';
import tooltip from "../navigation/Tooltip.vue";
import {ucfirst} from "../../../shared/helpers";
import {mapState} from 'pinia';
import {useMainStore} from '../../state/store';

export default {
    components: {
        tooltip,
        'flat-pickr': flatPickr,
        'svg-icon': SvgIcon,
    },
    props: {
        value: {
            type: [String, moment],
            default: null,
        },
        time: {
            type: Boolean,
            default: false,
        },
        sizeClass: String,
        disabled: Boolean,
        format: String,
        clearable: {
            type: Boolean,
            default: false,
        },
        disableWeekends: Boolean,
        disablePast: Boolean,
        disableToday: Boolean,
        showWeekNumbers: {
            type: Boolean,
            default: true,
        },
        placeholder: String,
        mode: {
            type: String,
            default: 'single',
        },
        showMonths: {
            type: Number,
            default: 1,
        },
        showWarningIcon: {type: Boolean, default: false},
        warningMessage: {type: String, default: null},
        enableAutoFormat: {type: Boolean, default: true},
        futureLimit: {
            type: [Date, Object, null],
            default: null,
        },
        pastLimit: {
            type: [Date, Object, null],
            default: null,
        },
        errors: Array,
        inputLabel: String,
        whiteBackground: Boolean,
    },
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            minWidthInput: '',
            config: {
                altInput: true,
                allowInput: true,
                altInputClass: 'grow border-0 ring-0 outline-none bg-inherit',
                formatDate: (date) => {
                    return moment(date)
                        .locale(this.mainStore.locale)
                        .format(this.getFormat());
                },
                parseDate: (datestr, format) => {
                    let possibleFormats = [
                        'DD/MM/YYYY', this.getFormat(), 'l', 'L', 'LLL', this.format, format,
                    ];

                    for (let i = 0; i < possibleFormats.length; i++) {
                        let date = moment(datestr, possibleFormats[i], this.mainStore.locale);
                        if (date.isValid()) {
                            return date.toDate();
                        }
                    }
                },
                disable: [
                    (date) => {
                        if (this.disablePast && moment().startOf('day').isAfter(date)) {
                            return true;
                        }

                        if (this.disableToday && moment().isSame(date, 'day')) {
                            return true;
                        }

                        if (this.pastLimit && moment(this.pastLimit).isAfter(date)) {
                            return true;
                        }

                        if (this.futureLimit && moment(this.futureLimit).isBefore(date)) {
                            return true;
                        }

                        if (this.disableWeekends) {
                            return (date.getDay() === 0 || date.getDay() === 6);
                        } else {
                            return false;
                        }
                    },
                ],
                weekNumbers: this.showWeekNumbers,
                showMonths: this.showMonths,
                enableTime: this.time,
                locale: this.mainStore.locale === 'en'
                    ? null
                    : require('flatpickr/dist/l10n/' + this.mainStore.locale + '.js').default[this.mainStore.locale],
                mode: this.mode,
                plugins: this.mode === 'range' ? [
                    new ShortcutButtonsPlugin({
                        button: [
                            {label: this.$t('analytics.today')},
                            {label: this.$t('analytics.yesterday')},
                            {label: this.$t('analytics.past_7_days')},
                            {label: this.$t('analytics.past_30_days')},
                            {label: this.$t('analytics.current_month')},
                            {label: this.$t('analytics.previous_month')},
                            {label: this.$t('analytics.current_year')},
                        ],
                        onClick: (index, fp) => {
                            let date;
                            switch (index) {
                                case 0:
                                    date = new Date();
                                    break;
                                case 1:
                                    date = moment().subtract(1, 'd').toDate();
                                    break;
                                case 2:
                                    date = [moment().subtract(1, 'w').toDate(), new Date()];
                                    break;
                                case 3:
                                    date = [moment().subtract(1, 'months').toDate(), new Date()];
                                    break;
                                case 4:
                                    date = [moment().startOf('month').toDate(), moment().endOf('month').toDate()];
                                    break;
                                case 5:
                                    date = [moment().subtract(1, 'months').startOf('month').toDate(), moment().subtract(1, 'months').endOf('month').toDate()];
                                    break;
                                case 6:
                                    date = [moment().startOf('year').toDate(), new Date()];
                                    break;
                            }

                            fp.setDate(date);

                            //Close the damn thing
                            fp.close();

                            this.$nextTick(() => {
                                //Set date again and trigger a change to visual change from-till in input
                                fp.setDate(date, true);
                            });
                        },
                    }),
                ] : '',
            },
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        date() {
            //Hacky way to change input width when there is a range selected :/
            if (this.mode === 'range' && this.value) {
                this.minWidthInput = document.querySelector('.flatpickr-input').value.length > 20 ? 'min-w-400' : ''
            }

            if (!this.enableAutoFormat) {
                return this.value ? moment(this.value).local().locale(this.locale).format(this.getFormat()) : null;
            }

            return this.value ? moment.utc(this.value).local().locale(this.locale).format(this.getFormat()) : null;
        },
        error() {
            if (!this.errors) {
                return null;
            }

            return Array.isArray(this.errors) ? this.errors[0] : this.errors;
        },
        invalidDateMessage() {
            return this.warningMessage ? this.warningMessage : this.$t('general.invalid_date');
        },
    },
    methods: {
        capitaliseFirst(text = '') {
            return ucfirst(text);
        },
        onEndChange(selectedDates, dateStr, instance) {
            if (this.mode === 'range') {
                return this.$emit('rangedate', selectedDates);
            } else {
                const value = selectedDates[0] || null;

                if (!value) {
                    return this.clearInput();
                }

                let stringValue = moment(value, this.getFormat(), this.locale);

                if (this.value && stringValue.isSame(moment.utc(this.value).local().locale(this.locale))) {
                    // The value is the same, don't execute this again!
                    return;
                }

                // Only when using convert back to utc
                // Avoid emitting the same value
                if (this.time) {
                    if (!moment(this.value)?.isSame(stringValue.utc())) {
                        this.$emit('input', stringValue.utc());
                    }
                } else {
                    if (!moment(this.value)?.isSame(moment.utc(stringValue.format('YYYY-MM-DD')))) {
                        //When you don't use time just use the date without time
                        this.$emit('input', moment.utc(stringValue.format('YYYY-MM-DD')));
                    }
                }
            }
        },
        handleFocus() {
            this.$refs.picker.$el.nextSibling.focus();
        },
        clearInput() {
            this.minWidthInput = '';
            this.$emit('input', null);
        },
        getFormat() {
            return this.format || (this.time ? 'LLL' : 'LL');
        },
    },
    watch: {
        'showWarningIcon'() {
            if (this.showWarningIcon) {
                this.$refs.picker.fp.altInput.classList.add('pl-40');
            } else {
                this.$refs.picker.fp.altInput.classList.remove('pl-40');
            }
        },
        'futureLimit'() {
            this.$refs.picker.fp.jumpToDate(new Date(this.futureLimit));
        },
        'pastLimit'() {
            this.$refs.picker.fp.jumpToDate(new Date(this.pastLimit));
        },
    }
};
</script>
<style>
.flatpickr-day {
    &:hover {
        @apply bg-primary-20 border-primary-20;
    }
    &.selected {
        @apply bg-primary border-primary;
        &:hover {
            @apply bg-primary border-primary;
        }
    }
    &.today:not(.selected) {
        @apply border-primary-20;
        &:hover {
            @apply bg-primary-20 border-primary-20 text-black;
        }
    }
}
</style>
